import axios, {AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse} from 'axios';
import * as Sentry from '@sentry/react';
import Router from 'next/router';
import {goToErrorPage, goToServiceMaintenancePage, KakaoAccountType, redirectKakaoAccount} from '@src/utils/route';
import {HTTP_CODE, UNREGISTER_CODE} from '@src/constants';
import {isKakaoTalk} from '@src/utils/common';
import {BannerPage} from '@src/enums/home';
import {ErrorPage} from '@src/enums/api';

const defaultTimeout = 30000;
const timeout = Number(process.env.NEXT_PUBLIC_NETWORK_TIMEOUT) || defaultTimeout;
const PLANNING_DETAIL_REGX = /^\/v1\/planning\/info\//;
export interface ApiResponse {
  code: number;
  msg: string;
}
export interface ApiErrorResponse {
  status: number;
  data: any;
  config?: any;
}

const {INTERNAL_SERVER_ERROR, UNKNOWN, MAX_5XX} = HTTP_CODE;

const client: AxiosInstance = axios.create({
  baseURL: '/wapi',
  timeout,
  withCredentials: true,
});

const apiSuccessHandler = <T>(res: AxiosResponse<T>) => {
  const {status, data, config} = res;
  const {data: requestData = ''} = config;
  console.log(`##axios_response:${config.method}:${config.url}`, status, requestData, data);
  return data;
};

const apiErrorHandler = (err: AxiosError): ApiErrorResponse | undefined => {
  const {message, config = {}, response = {status: UNKNOWN, data: {code: UNKNOWN, msg: 'Unkown Error'}}} = err;
  const {status, data} = response as ApiErrorResponse;
  const is5XXStatus = status >= INTERNAL_SERVER_ERROR && status <= MAX_5XX;

  console.log(`##axios_error:${config.method}:${config.url}`, status, data, message);
  if (status === HTTP_CODE.SERVICE_MAINTENANCE) {
    goToServiceMaintenancePage(data);
    return;
  }

  if (message === 'Network Error' || message.indexOf('timeout') >= 0) {
    goToErrorPage(ErrorPage.NETWORK);
    return;
  }

  if (status === HTTP_CODE.NOT_FOUND) {
    const {url} = config;
    if (url && PLANNING_DETAIL_REGX.test(url)) {
      Router.replace('/planning/done');
    } else {
      goToErrorPage(ErrorPage.NOT_FOUND);
    }
    return;
  }

  if (status === HTTP_CODE.BAD_REQUEST && config.url === '/v1/auths/login') {
    // _kawlt or SESSION 토큰이 없는 경우
    // 인앱 브라우저에서는 발생해서는 안되는 케이스
    Sentry.captureMessage('Redirect to kakao login page', {
      level: Sentry.Severity.Warning,
      extra: {
        baseURL: config.baseURL,
        url: config.url,
        status,
        data,
      },
    });
    redirectKakaoAccount(KakaoAccountType.LOGIN);
    return;
  }

  if (status === HTTP_CODE.UNAUTHORIZED) {
    if (data.code === UNREGISTER_CODE) {
      return;
    }

    if (isKakaoTalk()) {
      Sentry.captureMessage('Go to unauthorized page', {
        level: Sentry.Severity.Warning,
        extra: {
          baseURL: config.baseURL,
          url: config.url,
          status,
          data,
        },
      });
      goToErrorPage(ErrorPage.UNAUTHORIZED);
    } else {
      Sentry.captureMessage('Redirect to kakao logout page', {
        level: Sentry.Severity.Warning,
        extra: {
          baseURL: config.baseURL,
          url: config.url,
          status,
          data,
        },
      });
      redirectKakaoAccount(KakaoAccountType.LOGOUT);
    }
    return;
  }

  if (is5XXStatus) {
    Sentry.withScope(scope => {
      Object.entries(response).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
      scope.setLevel(Sentry.Severity.Warning);
      Sentry.captureMessage(message);
    });
  }
  // 임시적으로 위 케이스 이외에는 네트워크 에러 페이지로 이동
  goToErrorPage(ErrorPage.NETWORK);
};

export const api = <T>(url: string, config = {} as AxiosRequestConfig): Promise<T> => {
  const isMock = /^\/api/.test(url);
  return new Promise((resolve, reject) => {
    client({url, ...config, ...(isMock ? {baseURL: ''} : {})})
      .then(res => resolve(apiSuccessHandler<T>(res)))
      .catch(err => reject(apiErrorHandler(err)));
  });
};

export const apiGet = <T>(url: string) => api<T>(url);
export const apiPost = <T>(url: string, data?: any, config?: AxiosRequestConfig) => api<T>(url, {method: 'post', data, ...config});
export const apiDelete = <T>(url: string, data?: any, config?: AxiosRequestConfig) => api<T>(url, {method: 'delete', data, ...config});
export const apiPut = <T>(url: string, data?: any, config?: AxiosRequestConfig) => api<T>(url, {method: 'put', data, ...config});
export const apiPatch = <T>(url: string, data?: any, config?: AxiosRequestConfig) => api<T>(url, {method: 'patch', data, ...config});

export const apiFetch = (url: string) => fetch(url).then(res => res.json());

export const getBannerUrl = (page: BannerPage, size: number) => `/v1/banners/${page}/on_list?size=${size}`;
